import { Component, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomDocumentsReviewComponent } from 'src/app/components/custom-documents-review/custom-documents-review.component';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { PreInvoicingStateEnums } from 'src/app/enum/pre-invoicing-state-enums';
import { ItemFilterModel } from 'src/app/models/filters/item-filter.model';
import { ElectronicInvoiceInterfaceModel } from 'src/app/models/invoicing/electronic-invoice-interface.model';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { PendingSyncDocumentTableModel } from 'src/app/models/tables/invoicing/pending-sync-document-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { BranchEnums } from './../../../../enum/branch-enums';
import { ElectronicDocumentService } from './../../../../services/invoicing/electronic-document.service';

@Component({
  selector: 'app-synchronizer-electronic-invoice-interface',
  templateUrl: './synchronizer-electronic-invoice-interface.component.html',
  styleUrls: ['./synchronizer-electronic-invoice-interface.component.scss'],
  standalone: false
})
export class SynchronizerElectronicInvoiceInterfaceComponent implements OnInit {

  public readonly config = inject(DynamicDialogConfig);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly electronicDocumentService = inject(ElectronicDocumentService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly ref = inject(DynamicDialogRef, { optional: true });

  @ViewChild('pendingSyncDocumentModal') pendingSyncDocumentModalComponent?: CustomModalComponent | null = null;
  @Output() newElectronicInvoiceInterface = new EventEmitter<ElectronicInvoiceInterfaceModel>();

  animationLoad: boolean = false;
  ascendant: boolean = true;
  branchId: number = 0;
  buttonLabel: string = 'Continuar';
  codeNameBranch: string | undefined = '';
  codeNameEnterprise: string | undefined = '';
  columnSort: string | null = null;
  componentPendingSyncDocument = CustomDocumentsReviewComponent;
  data: PagedResponseModel<PendingSyncDocumentTableModel> = new PagedResponseModel<PendingSyncDocumentTableModel>();
  dataRecovery: { listBranches: any[], listEnterprises: any[] };
  disabledStart: boolean = false;
  electronicInvoiceInterfaceContract: ElectronicInvoiceInterfaceModel = new ElectronicInvoiceInterfaceModel();
  formElectronicDocuments: UntypedFormGroup = new UntypedFormGroup({});
  header: ColumnResponseModel[] = [];
  infoMessage: string = 'Los siguientes documentos no cumplen con los requisitos para envío a la DIAN';
  inputsForPendingSyncDocumentModal$ = new BehaviorSubject<any>({
    infoMessage: '',
    buttonLabel: '',
    headers: [],
    data: []
  });
  limit: number = 10;
  listBranch: Array<ItemFilterModel> = new Array<ItemFilterModel>();
  listEnterprise: Array<ItemFilterModel> = new Array<ItemFilterModel>();
  listObservers: Array<Subscription> = new Array<Subscription>();
  listType = BranchEnums.sucursalesUsuario;
  mainText: string = "";
  nameBranch: string = '';
  nameEnterprise: string = '';
  output$ = new Subject<any>();
  page: number = 1;
  percentage: number = 0;
  permissionUpdatable: boolean = false;
  preInvoicingAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>();
  startProcess: boolean = false;
  subscription: Subscription;
  visibleAlerts: boolean = false;

  ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }

    this.subscription = input$.subscribe((inputs: any) => {
      this.electronicInvoiceInterfaceContract = inputs.electronicInvoiceInterfaceModel;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.dataRecovery = inputs.dataRecovery;

      this.nameBranch = this.dataRecovery.listBranches
        .find(x => x.value === this.electronicInvoiceInterfaceContract.branchId)?.label;

      this.nameEnterprise = this.dataRecovery.listEnterprises
        .find(x => x.value === this.electronicInvoiceInterfaceContract.enterpriseId)?.label;

      this.mainText =
        this.electronicInvoiceInterfaceContract.electronicInvoiceInterfaceId === 0
          ? 'Procesando...' : 'Consultando...';
    });

    this.subscription.add(
      this.output$.subscribe(async ({ type, payload }) => {
        if (type === 'onExport') {
          await this.exportPendingSyncDocuments();
        } else if (type === 'onPageChange') {
          await this.onPageChangePendingSyncDocuments(payload);
        } else {
          console.warn('Tipo de evento desconocido:', type);
        }
      })
    );
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  cancel() {
    this.ref?.close();
  }

  async exportPendingSyncDocuments() {
    try {
      const { enterpriseId } = this.electronicInvoiceInterfaceContract;
      const blob = await firstValueFrom(this.electronicDocumentService.exportPendingSyncDocuments(
        this.columnSort, this.ascendant, enterpriseId
      ));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Documentos Pendientes por Sincronizar.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles de la documentos pendientes por sincronizar.', error);
    }
  }

  async getColumnsForPendingSyncDocument() {
    try {
      if (this.header.length === 0) {
        const response = await firstValueFrom(this.electronicDocumentService.getColumnsForPendingSyncDocument());
        this.header = response;
        this.updateInputs();
      }
    } catch (error) {
      console.error('Error al obtener las columnas de documentos pendientes de sincronización.', error);
    }
  }

  async getElectronicDocuments() {
    try {
      const { enterpriseId } = this.electronicInvoiceInterfaceContract;
      const response = await firstValueFrom(this.electronicDocumentService.getElectronicDocuments(enterpriseId));
      if (response.alertType == PreInvoicingStateEnums.ok) {
        const accepted = await this.confirmDialogService.confirm(response.message);
        if (accepted) {
          this.startProcess = true;
          this.preInvoicingAlerts = await firstValueFrom(this.electronicDocumentService.sendElectronicDocuments(enterpriseId));
          this.visibleAlerts = true;

          const hasErrorsOrAlerts = this.preInvoicingAlerts.some(x =>
            x.alertType == PreInvoicingStateEnums.error || x.alertType == PreInvoicingStateEnums.alerta
          );

          if (!hasErrorsOrAlerts) {
            const output$ = this.config.data?.output$;
            output$?.next({ type: 'update', payload: this.electronicInvoiceInterfaceContract });
          }
        }
      } else if (response.alertType == PreInvoicingStateEnums.error) {
        this.toastHandlerService.showToast('error', response.message, 'Atención');
      }
    } catch (error) {
      console.error('Error al conectar con el servicio de la DIAN.', error);
    }
  }

  async getPendingSyncDocumentsWithPagination() {
    try {
      const { enterpriseId } = this.electronicInvoiceInterfaceContract;
      const response = await firstValueFrom(this.electronicDocumentService.getPendingSyncDocumentsWithPagination(
        this.page, this.limit, this.columnSort, this.ascendant, enterpriseId
      ));
      this.data = response;
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener los documentos pendientes de sincronización.', error);
    }
  }

  async onPageChangePendingSyncDocuments(event: { page: number; pageSize: number; sortField?: string; sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField ?? null;
    this.ascendant = event.sortOrder === 1;
    await this.getPendingSyncDocumentsWithPagination();
  }

  async processExistingInterface(id: number) {
    try {
      this.startProcess = true;
      this.visibleAlerts = true;
      const response = await firstValueFrom(this.electronicDocumentService.checkStatusDIAN(id));
      this.preInvoicingAlerts = response || [];
    } catch (error) {
      this.startProcess = false;
      this.visibleAlerts = false;
      this.disabledStart = false;
      console.error('Error al consultar estado en la DIAN.', error);
    }
  }

  async syncUp() {
    const { electronicInvoiceInterfaceId } = this.electronicInvoiceInterfaceContract;
    this.disabledStart = true;
    try {
      if (electronicInvoiceInterfaceId !== 0) {
        await this.processExistingInterface(electronicInvoiceInterfaceId);
        return;
      }
      await this.getPendingSyncDocumentsWithPagination();
      if (this.data?.totalItems > 0) {
        await this.getColumnsForPendingSyncDocument();
        this.pendingSyncDocumentModalComponent?.show();
      } else {
        await this.getElectronicDocuments();
      }
    } catch (error) {
      this.disabledStart = false;
      console.error('error al procesar la solicitud.', error);
    }
  }

  updateInputs() {
    this.inputsForPendingSyncDocumentModal$.next({
      infoMessage: this.infoMessage,
      buttonLabel: this.buttonLabel,
      headers: this.header,
      data: this.data
    });
  }
}
